import * as Yup from 'yup'
import type { ComposableFormField } from '../../../models/organisms/ComposableForm'
import type { SelectOption } from '../../atoms/Fields/Select/Select.component'
import { CRM_FIXED_MAPPINGS, CRM_CONSENT_MAPPING } from './ComposableForm.constants'

export type CrmFieldMapping = {
  FormFieldName?: string
  DataverseFieldName: string
  DataverseFieldValue?: string
}

export function buildInitialValues(
  fields: ComposableFormField[],
  searchParams: URLSearchParams | null
): Record<string, string> {
  return fields.reduce<Record<string, string>>((acc, field) => {
    const urlValue = field.compileWithUrlParams
      ? searchParams?.get(field.inputId)
      : null
    acc[field.inputId] = urlValue || field.dafaultValue || ''
    return acc
  }, {})
}

export function isFieldHidden(
  field: ComposableFormField,
  urlParamValue: string | null
): boolean {
  if (field.requiredField) return false
  if (!field.hiddenField) return false
  if (field.compileWithUrlParams && urlParamValue) return false
  return true
}

export function buildValidationSchema(
  fields: ComposableFormField[],
  t: (key: string) => string,
  searchParams: URLSearchParams | null
) {
  const shape: Record<string, Yup.AnySchema> = {}

  fields.forEach((field) => {
    const urlParamValue = field.compileWithUrlParams ? searchParams?.get(field.inputId) ?? null : null
    if (isFieldHidden(field, urlParamValue)) return

    let validator = Yup.string()

    if (field.inputType === 'email') {
      validator = validator.email(t('composableForm.errorEmail'))
    }

    if (field.inputType === 'number') {
      validator = validator.matches(/^[0-9+\s]*$/, t('composableForm.errorNumber'))
    }

    if (field.requiredField) {
      validator = validator.required(t('composableForm.errorRequired'))
    }

    shape[field.inputId] = validator
  })

  shape.privacy = Yup.boolean().oneOf([true], t('composableForm.errorPrivacy'))

  return Yup.object().shape(shape)
}

export function parseSelectOptions(raw: string | null, emptyLabel: string): SelectOption[] {
  const options: SelectOption[] = [{ value: '', label: emptyLabel }]

  if (!raw) return options

  raw.split('\n').forEach((line) => {
    const trimmed = line.trim()
    if (!trimmed) return

    const colonIndex = trimmed.indexOf(':')
    if (colonIndex > -1) {
      const value = trimmed.substring(0, colonIndex).trim()
      const label = trimmed.substring(colonIndex + 1).trim()
      options.push({ value, label })
    } else {
      options.push({ value: trimmed, label: trimmed })
    }
  })

  return options
}

export function buildCrmMappings(
  fields: ComposableFormField[],
  values: Record<string, string | boolean>
): CrmFieldMapping[] {
  const fieldMappings: CrmFieldMapping[] = fields
    .filter((field) => {
      const value = values[field.inputId]
      return typeof value === 'string' && value !== ''
    })
    .map((field) => ({
      FormFieldName: field.inputId,
      DataverseFieldName: field.inputId,
    }))

  return [...fieldMappings, ...CRM_FIXED_MAPPINGS, CRM_CONSENT_MAPPING]
}
